import React, { useState } from 'react';
import { VscSearch, VscRefresh, VscChevronLeft, VscChevronRight } from 'react-icons/vsc';
import { useAdminFailedJobs } from '../hooks/useAdminFailedJobs';
import { useTranslate } from '../hooks/useTranslate';
import type { AdminJobListItem } from '../types/adminJobs';

const STATUS_STYLES: Record<string, string> = {
    FAILED: 'bg-red-100 text-red-700 border-red-200',
    CANCELLED: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

function StatusBadge({ status }: { status: string }) {
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-700 border-gray-200'}`}>
            {status}
        </span>
    );
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const AdminFailedJobsTab: React.FC = () => {
    const { t } = useTranslate();
    const [emailInput, setEmailInput] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const { data, loading, error, filters, setFilter, setFilterDebounced, goToPage, refetch, currentPage, totalPages, hasMore } = useAdminFailedJobs();

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmailInput(e.target.value);
        setFilterDebounced('user_email', e.target.value || undefined);
    };

    const handleSourceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setFilter('source_type', e.target.value || undefined);
    };

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setFilter('status_filter', e.target.value || undefined);
    };

    const handleFromDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFromDate(e.target.value);
        setFilter('from_date', e.target.value || undefined);
    };

    const handleToDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        setToDate(e.target.value);
        setFilter('to_date', e.target.value || undefined);
    };

    const handleSort = (field: typeof filters.order_by) => {
        if (filters.order_by === field) {
            setFilter('order_direction', filters.order_direction === 'asc' ? 'desc' : 'asc');
        } else {
            setFilter('order_by', field);
            setFilter('order_direction', 'desc');
        }
    };

    const sortIndicator = (field: string) => {
        if (filters.order_by !== field) return null;
        return filters.order_direction === 'asc' ? ' ↑' : ' ↓';
    };

    return (
        <div className="p-4 sm:p-6 space-y-4">
            {/* Filter Bar */}
            <div className="flex flex-wrap gap-3 items-end bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex-1 min-w-[180px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('User Email')}</label>
                    <div className="relative">
                        <VscSearch className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                        <input type="text" value={emailInput} onChange={handleEmailChange} placeholder={t('Filter by email...')} className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none" />
                    </div>
                </div>
                <div className="min-w-[130px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('Source Type')}</label>
                    <select onChange={handleSourceTypeChange} className="w-full py-2 px-3 text-sm bg-white text-gray-700 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none">
                        <option value="">All</option>
                        <option value="file">File</option>
                        <option value="databricks">Databricks</option>
                    </select>
                </div>
                <div className="min-w-[140px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('Status')}</label>
                    <select onChange={handleStatusChange} className="w-full py-2 px-3 text-sm bg-white text-gray-700 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none">
                        <option value="">Failed &amp; Cancelled</option>
                        <option value="FAILED">Failed Only</option>
                        <option value="CANCELLED">Cancelled Only</option>
                    </select>
                </div>
                <div className="min-w-[140px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('From')}</label>
                    <input type="date" value={fromDate} onChange={handleFromDate} className="w-full py-2 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none" />
                </div>
                <div className="min-w-[140px]">
                    <label className="block text-xs font-medium text-gray-500 mb-1">{t('To')}</label>
                    <input type="date" value={toDate} onChange={handleToDate} className="w-full py-2 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-200 focus:border-red-400 outline-none" />
                </div>
                <button onClick={refetch} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors" title={t('Refresh')}>
                    <VscRefresh className="w-4 h-4" />
                </button>
            </div>

            {/* Error State */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                    {error}
                    <button onClick={refetch} className="ml-3 underline font-medium">{t('Retry')}</button>
                </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-red-600" onClick={() => handleSort('user_email')}>{t('User')}{sortIndicator('user_email')}</th>
                                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('Source')}</th>
                                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('Dataset')}</th>
                                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-red-600" onClick={() => handleSort('status')}>{t('Status')}{sortIndicator('status')}</th>
                                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('Phase')}</th>
                                <th className="text-left px-4 py-3 font-medium text-gray-600">{t('Error')}</th>
                                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-red-600" onClick={() => handleSort('created_at')}>{t('Created')}{sortIndicator('created_at')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading && (
                                <tr><td colSpan={7} className="text-center py-12 text-gray-400">{t('Loading...')}</td></tr>
                            )}
                            {!loading && data && data.jobs.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-12 text-gray-400">{t('No failed jobs found')}</td></tr>
                            )}
                            {!loading && data?.jobs.map((job: AdminJobListItem) => (
                                <tr key={job.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 font-medium text-gray-700 max-w-[180px] truncate" title={job.user_email}>{job.user_email}</td>
                                    <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate" title={job.source_identifier}>{job.source_identifier}</td>
                                    <td className="px-4 py-3 text-gray-600">{job.dataset_name ?? '—'}</td>
                                    <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                                    <td className="px-4 py-3 text-gray-500 capitalize">{job.current_phase ?? '—'}</td>
                                    <td className="px-4 py-3 text-gray-500 max-w-[220px] truncate" title={job.error_message ?? undefined}>{job.error_message ?? '—'}</td>
                                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(job.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {data && data.total_count > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
                        <span className="text-xs text-gray-500">
                            {t('Showing')} {(filters.offset ?? 0) + 1}–{Math.min((filters.offset ?? 0) + (filters.limit ?? 20), data.total_count)} {t('of')} {data.total_count}
                        </span>
                        <div className="flex items-center gap-2">
                            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 0} className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                                <VscChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="text-xs text-gray-600 font-medium">{currentPage + 1} / {totalPages}</span>
                            <button onClick={() => goToPage(currentPage + 1)} disabled={!hasMore} className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                                <VscChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminFailedJobsTab;
